import { useState, useMemo, useEffect } from "react";
import {
  AgendaCustomizationState,
  CoverStyle,
  EngravingPosition,
  DEFAULT_PRICING,
} from "@/types/agendaCustomization";
import { CoverStyleSelector } from "./CoverStyleSelector";
import { DesignGrid } from "./DesignGrid";
import { EngravingSection } from "./EngravingSection";
import { ProductPreview } from "./ProductPreview";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";

interface AgendaCustomizerProps {
  basePrice: number;
  onCustomizationChange: (customization: AgendaCustomizationState, totalPrice: number) => void;
  onAddToCart?: () => void;
}

export const AgendaCustomizer = ({
  basePrice,
  onCustomizationChange,
  onAddToCart,
}: AgendaCustomizerProps) => {
  const [customization, setCustomization] = useState<AgendaCustomizationState>({
    coverStyle: "premium",
    hasEngraving: false,
    hasDeluxeSeparators: false,
    foilOptions: [],
  });

  // Calcular precio de personalización
  const customizationPrice = useMemo(() => {
    let price = 0;

    // Separadores deluxe
    if (customization.hasDeluxeSeparators) {
      price += DEFAULT_PRICING.deluxeSeparatorsPrice;
    }

    // Opciones de foil
    if (customization.foilOptions && customization.foilOptions.length > 0) {
      price += customization.foilOptions.length * DEFAULT_PRICING.foilPrice;
    }

    // Vectorización de logo
    if (customization.hasLogo && customization.logoFile) {
      price += DEFAULT_PRICING.logoVectorizationPrice;
    }

    return price;
  }, [customization]);

  // Notificar cambios al padre cuando cambie la personalización o el precio
  useEffect(() => {
    const totalPrice = basePrice + customizationPrice;
    onCustomizationChange(customization, totalPrice);
  }, [customization, customizationPrice, basePrice, onCustomizationChange]);

  // Actualizar customización
  const updateCustomization = (updates: Partial<AgendaCustomizationState>) => {
    const newCustomization = { ...customization, ...updates };
    setCustomization(newCustomization);
  };

  // Handlers
  const handleCoverStyleChange = (style: CoverStyle) => {
    updateCustomization({ coverStyle: style });
  };

  const handleDesignSelect = (designId: string) => {
    updateCustomization({ designId, customDesignFile: undefined });
  };

  const handleCustomDesignUpload = (file: File | undefined) => {
    updateCustomization({ customDesignFile: file, designId: undefined });
  };

  const handleEngravingToggle = (enabled: boolean) => {
    updateCustomization({
      hasEngraving: enabled,
      engravingText: enabled ? customization.engravingText : undefined,
      engravingPosition: enabled ? customization.engravingPosition || "centro" : undefined,
      engravingTypography: enabled ? customization.engravingTypography : undefined,
      engravingColor: enabled ? customization.engravingColor : undefined,
    });
  };

  const handleEngravingTextChange = (text: string) => {
    updateCustomization({ engravingText: text });
  };

  const handlePositionChange = (position: EngravingPosition) => {
    updateCustomization({ engravingPosition: position });
  };

  const handleTypographyChange = (typography: string) => {
    updateCustomization({ engravingTypography: typography });
  };

  const handleColorChange = (color: string) => {
    updateCustomization({ engravingColor: color });
  };

  const handleLogoToggle = (enabled: boolean) => {
    updateCustomization({
      hasLogo: enabled,
      logoFile: enabled ? customization.logoFile : undefined,
    });
  };

  const handleLogoUpload = (file: File) => {
    updateCustomization({ logoFile: file });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Columna izquierda - Preview */}
      <div className="lg:col-span-1 order-1 lg:order-1">
        <ProductPreview
          coverStyle={customization.coverStyle}
          designId={customization.designId}
          engravingText={customization.engravingText}
          engravingPosition={customization.engravingPosition}
          engravingTypography={customization.engravingTypography}
          engravingColor={customization.engravingColor}
          customDesignFile={customization.customDesignFile}
        />
      </div>

      {/* Columna derecha - Formulario */}
      <div className="lg:col-span-2 order-2 lg:order-2 space-y-6">
        {/* Selector de estilo de cubierta */}
        <CoverStyleSelector
          selectedStyle={customization.coverStyle}
          onStyleChange={handleCoverStyleChange}
        />

        {/* Grid de diseños */}
        <DesignGrid
          selectedDesignId={customization.designId}
          customDesignFile={customization.customDesignFile}
          onDesignSelect={handleDesignSelect}
          onCustomDesignUpload={handleCustomDesignUpload}
        />

        {/* Sección de grabado */}
        <EngravingSection
          hasEngraving={customization.hasEngraving}
          engravingText={customization.engravingText}
          engravingPosition={customization.engravingPosition}
          engravingTypography={customization.engravingTypography}
          engravingColor={customization.engravingColor}
          hasLogo={customization.hasLogo}
          onEngravingToggle={handleEngravingToggle}
          onEngravingTextChange={handleEngravingTextChange}
          onPositionChange={handlePositionChange}
          onTypographyChange={handleTypographyChange}
          onColorChange={handleColorChange}
          onLogoToggle={handleLogoToggle}
          onLogoUpload={handleLogoUpload}
        />

        {/* Precio de personalización */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-serif text-gray-600 dark:text-gray-400">
              Costo adicional por personalización
            </span>
            <span
              className={`text-lg font-bold ${
                customizationPrice > 0
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-700 dark:text-gray-300"
              }`}
            >
              $ {customizationPrice.toFixed(2)} MXN
            </span>
          </div>

          {/* Desglose de precios */}
          {customizationPrice > 0 && (
            <div className="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">
              {customization.hasDeluxeSeparators && (
                <div className="flex justify-between">
                  <span>• Separadores Deluxe</span>
                  <span>+${DEFAULT_PRICING.deluxeSeparatorsPrice.toFixed(2)}</span>
                </div>
              )}
              {customization.foilOptions && customization.foilOptions.length > 0 && (
                <div className="flex justify-between">
                  <span>
                    • Foil ({customization.foilOptions.length} opciones)
                  </span>
                  <span>
                    +$
                    {(
                      customization.foilOptions.length * DEFAULT_PRICING.foilPrice
                    ).toFixed(2)}
                  </span>
                </div>
              )}
              {customization.hasLogo && customization.logoFile && (
                <div className="flex justify-between">
                  <span>• Vectorización de logo</span>
                  <span>+${DEFAULT_PRICING.logoVectorizationPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botón de agregar al carrito */}
        {onAddToCart && (
          <Button
            onClick={onAddToCart}
            size="lg"
            className="w-full bg-[#8B5A6C] hover:bg-[#6F4656] text-white transition-all"
          >
            <ShoppingCart className="mr-2 h-5 w-5" />
            Agregar al carrito
          </Button>
        )}
      </div>
    </div>
  );
};
